import {useMemo, lazy} from 'react';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';

const GuidebookView = lazy(() =>
  import('pages/GuidebookView/GuidebookView').then(m => ({default: m.GuidebookView})),
);
const GuidebookHtmlView = lazy(() =>
  import('pages/GuidebookHtmlView/GuidebookHtmlView').then(m => ({
    default: m.GuidebookHtmlView,
  })),
);
const GuidebookPdfView = lazy(() =>
  import('pages/GuidebookPdfView/GuidebookPdfView').then(m => ({
    default: m.GuidebookPdfView,
  })),
);
const HousingGuidebooks = lazy(() =>
  import('pages/HousingGuidebooksView/HousingGuidebooksView').then(m => ({
    default: m.HousingGuidebooksView,
  })),
);
const HousingGuidebookView = lazy(() =>
  import('pages/HousingGuidebooksView/GuidebookView').then(m => ({
    default: m.GuidebookView,
  })),
);
const HousingGuidebooksLayout = lazy(() =>
  import('pages/HousingGuidebooksView/HousingGuidebooksLayout').then(m => ({
    default: m.HousingGuidebooksLayout,
  })),
);
const HousingGuidebookDocView = lazy(() =>
  import('pages/HousingGuidebooksView/HousingGuidebookDocView').then(m => ({
    default: m.HousingGuidebookDocView,
  })),
);
const ChatView = lazy(() => import('pages/ChatView').then(m => ({default: m.ChatView})));
const TravelGuideView = lazy(() =>
  import('pages/TravelGuideView/TravelGuideView').then(m => ({
    default: m.TravelGuideView,
  })),
);
const Payments = lazy(() =>
  import('pages/Payments/Payments').then(m => ({default: m.Payments})),
);
const CheckoutView = lazy(() =>
  import('pages/Checkout/CheckoutView').then(m => ({default: m.default})),
);
const OfferView = lazy(() =>
  import('pages/OfferView/OfferView').then(m => ({default: m.OfferView})),
);
const OfferSlugRedirectView = lazy(() =>
  import('pages/OfferSlugRedirectView').then(m => ({
    default: m.OfferSlugRedirectView,
  })),
);
const LuggageStorageView = lazy(() =>
  import('pages/LuggageStorageView').then(m => ({default: m.LuggageStorageView})),
);
import {
  useInitGuestTaxes,
  usePath,
  useInitPropertyProtection,
  useSetPreviousPage,
  useGuestGroup,
} from 'hooks';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useBrandingTheme} from 'context/brandingTheme';
import {useSummary} from 'context/summary';
import {useClearRegistrationReturnOutsideFlow} from 'hooks/useRegistrationReturn';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {PATHS, addPersonalDataFormRoute, isRecoveryPathname} from 'routes/constants';
import {HomeView} from 'pages/HomeView';
import RootLayout from './components/RootLayout';
import ConnectionErrorsHandler from 'components/ConnectionErrorsHandler';
import {WelcomeRoute} from 'pages/WelcomeScreen';
import {GuestRegistrationView} from 'pages/GuestRegistrationView';
import {InstantCheckInView} from 'pages/InstantCheckIn';
import DepositView from 'pages/DepositView';
import RemoteAccessView from 'pages/RemoteAccess';
import {TaxesTouristView} from 'pages/Taxes';
import {getIVRoutes} from 'routes/IVRoutes';
import {useChatSettings} from 'hooks/useChatSettings';
import {isTravelGuideEnabled} from 'utils/travelGuide';

const AddPersonalDataForm = lazy(() => import('pages/AddPersonalDataForm'));
const FAQView = lazy(() => import('./pages/FAQView'));
const SearchReservation = lazy(() => import('pages/SearchReservation'));

const PaymentsView = lazy(() =>
  import('pages/Payments').then(m => ({default: m.PaymentsView})),
);

const BeforeYouLeaveView = lazy(() =>
  import('./pages/Checkout').then(m => ({default: m.BeforeYouLeaveView})),
);
const RateYourStayView = lazy(() =>
  import('./pages/Checkout').then(m => ({default: m.RateYourStayView})),
);
const ThanksView = lazy(() =>
  import('./pages/Checkout').then(m => ({default: m.ThanksView})),
);

const OfferListView = lazy(() =>
  import('pages/OfferListView').then(m => ({default: m.OfferListView})),
);
const MyRequestsView = lazy(() =>
  import('pages/MyRequestsView').then(m => ({default: m.MyRequestsView})),
);
const OAuthCallback = lazy(() =>
  import('pages/Auth/views/OAuthCallback').then(m => ({default: m.OAuthCallback})),
);

const PropertyLinkLayout = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({default: m.PropertyLinkLayout})),
);
const BookingCodeView = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({default: m.BookingCodeView})),
);
const CheckInDateView = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({default: m.CheckInDateView})),
);
const GuestNameView = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({default: m.GuestNameView})),
);
const DirectReservationView = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({
    default: m.DirectReservationView,
  })),
);
const NewReservationRoute = lazy(() =>
  import('pages/SearchReservation/Views').then(m => ({
    default: m.NewReservationRoute,
  })),
);

const ManageEsimView = lazy(() =>
  import('pages/esim/ManageEsimView').then(m => ({default: m.ManageEsimView})),
);
const ChoosePlanView = lazy(() =>
  import('pages/esim/ChoosePlanView').then(m => ({default: m.ChoosePlanView})),
);
const MyEsimsView = lazy(() =>
  import('pages/esim/MyEsimsView').then(m => ({default: m.MyEsimsView})),
);

const VerifyOnHousingView = lazy(() =>
  import('pages/VerifyOnHousing').then(m => ({default: m.VerifyOnHousingView})),
);
const FindReservationsByNameView = lazy(() =>
  import('pages/FindReservationsByName').then(m => ({
    default: m.FindReservationsByNameView,
  })),
);

function AppRouter() {
  useInitGuestTaxes();
  useInitPropertyProtection();
  useSetPreviousPage();
  useClearRegistrationReturnOutsideFlow();
  const {isChatEnabled, isLoading: isChatSettingsLoading} = useChatSettings();
  const {isTemplateInitialized} = useBrandingTheme();
  const {getFullPath} = usePath();
  const {housing, isLoading: isReservationLoading} = useReservation();
  const {isLoading: isGuestsGroupLoading} = useGuestGroup();
  const {isLoading, data: summaryData} = useSummary();
  const {isGuestSchemaInitialLoading} = useSchema();
  const {isCheckoutEnabled} = useCheckoutSettings();
  const isUpsellingRouteAvailable = isLoading || Boolean(summaryData?.upselling?.active);
  const isTravelGuideRouteAvailable =
    isUpsellingRouteAvailable &&
    (isLoading || isTravelGuideEnabled(summaryData?.country_code));

  const {pathname} = useLocation();
  const isRecoveryRoute = isRecoveryPathname(pathname);
  const isLoadingMinimalData =
    (!isTemplateInitialized ||
      isLoading ||
      isReservationLoading ||
      isGuestsGroupLoading ||
      isGuestSchemaInitialLoading) &&
    !isRecoveryRoute;

  const routes = useMemo(() => {
    return (
      <>
        <Route element={<RootLayout isLoading={isLoadingMinimalData} />}>
          <Route index element={<HomeView />} />
          <Route path={PATHS.guestRegistration} element={<GuestRegistrationView />} />

          <Route path={PATHS.auth.root}>
            <Route index element={<InstantCheckInView />} />
            <Route path={PATHS.auth.login} element={<InstantCheckInView />} />
            <Route
              path={PATHS.auth.createAccount}
              element={<InstantCheckInView entry="createAccount" />}
            />
          </Route>
          {addPersonalDataFormRoute.map((route, index) => {
            return <Route key={index} path={route} element={<AddPersonalDataForm />} />;
          })}
          <Route path={PATHS.deposits} element={<DepositView />}>
            <Route path=":id" element={<DepositView />} />
          </Route>
          <Route path={PATHS.payments.main} element={<Payments />}>
            <Route index path="*" element={<PaymentsView />} />
          </Route>
          {isCheckoutEnabled && (
            <Route path={PATHS.checkout.main} element={<CheckoutView />}>
              <Route index element={<BeforeYouLeaveView />} />
              <Route path={PATHS.checkout.rateYourStay} element={<RateYourStayView />} />
              <Route path={PATHS.checkout.thanks} element={<ThanksView />} />
            </Route>
          )}
          <Route path={PATHS.taxes.main} element={<TaxesTouristView />} />

          {getIVRoutes({
            isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
          })}
          <Route path={PATHS.remoteAccess} element={<RemoteAccessView />} />
          <Route path={PATHS.luggage} element={<LuggageStorageView />} />
          {isTravelGuideRouteAvailable && (
            <Route path={PATHS.travelGuide} element={<TravelGuideView />} />
          )}
          <Route path={PATHS.guidebook} element={<GuidebookView />}>
            <Route path=":id" element={<GuidebookView />} />
          </Route>
          <Route path={PATHS.guidebookHtml}>
            <Route path=":id" element={<GuidebookHtmlView />} />
          </Route>
          <Route path={PATHS.guidebookPdf}>
            <Route path=":id" element={<GuidebookPdfView />} />
          </Route>
          <Route path={PATHS.housingGuidebooks} element={<HousingGuidebooksLayout />}>
            <Route index element={<HousingGuidebooks />} />
            <Route
              path={`${PATHS.guidebookHtml}/:id`}
              element={<HousingGuidebookDocView variant="html" />}
            />
            <Route
              path={`${PATHS.guidebookPdf}/:id`}
              element={<HousingGuidebookDocView variant="pdf" />}
            />
            <Route path=":id" element={<HousingGuidebookView />} />
          </Route>
          <Route path={PATHS.faq} element={<FAQView />} />
          <Route
            path={PATHS.chat}
            element={
              isChatSettingsLoading ? null : isChatEnabled ? (
                <ChatView />
              ) : (
                <Navigate to={getFullPath(PATHS.home)} replace />
              )
            }
          />
          <Route element={<PropertyLinkLayout />}>
            <Route path={PATHS.searchReservation.main}>
              <Route index element={<SearchReservation />} />
            </Route>
            <Route
              path={PATHS.searchReservation.bookingCode}
              element={<BookingCodeView />}
            />
            <Route
              path={PATHS.searchReservation.checkingDate}
              element={<CheckInDateView />}
            />
            <Route path={PATHS.searchReservation.guestName} element={<GuestNameView />} />
          </Route>
          <Route
            path={PATHS.searchReservation.welcomeScreen}
            element={<WelcomeRoute />}
          />
          <Route
            path={PATHS.searchReservation.newReservation}
            element={<NewReservationRoute />}
          />
          <Route path={PATHS.esim.manage} element={<ManageEsimView />} />
          <Route path={PATHS.esim.choosePlan} element={<ChoosePlanView />} />
          <Route path={PATHS.esim.my}>
            <Route index path="*" element={<MyEsimsView />} />
          </Route>
          <Route path={PATHS.recommendations.main}>
            <Route index element={<OfferListView />} />
            <Route path={PATHS.recommendations.requests} element={<MyRequestsView />} />
            <Route path={PATHS.recommendations.details.main} element={<OfferView />} />
            <Route path="*" element={<Navigate to={getFullPath(PATHS.home)} />} />
          </Route>
          <Route
            path="*"
            element={
              isLoadingMinimalData ? null : (
                <Navigate to={getFullPath(PATHS.home)} replace />
              )
            }
          />
        </Route>

        <Route path={PATHS.verifyOnHousing} element={<VerifyOnHousingView />} />
        <Route
          path={PATHS.findReservationsByName}
          element={<FindReservationsByNameView />}
        />
        <Route
          path={PATHS.searchReservation.directReservation}
          element={<DirectReservationView />}
        />
      </>
    );
  }, [
    getFullPath,
    housing?.is_liveness_detection_enabled,
    isLoadingMinimalData,
    isCheckoutEnabled,
    isChatEnabled,
    isChatSettingsLoading,
    isTravelGuideRouteAvailable,
  ]);

  return (
    <Routes>
      <Route path={PATHS.oauth.google} element={<OAuthCallback />} />
      <Route path="/" element={<ConnectionErrorsHandler />}>
        <Route
          path="/:token/:slug"
          element={<RootLayout isLoading={isLoadingMinimalData} />}
        >
          <Route index element={<OfferSlugRedirectView />} />
        </Route>
        <Route path={PATHS.home}>{routes}</Route>
      </Route>
    </Routes>
  );
}
export default AppRouter;
